import { useEffect, useState } from "react";
import { Receipt, MessageCircle, CheckCheck, X, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr, currentMonth, monthLabel, formatApiError, FEE_LABELS } from "../../lib/api";
import { MonthNav } from "../../components/MonthNav";
import { PAYMENT_MODES } from "../../data/content";
import { ConfirmDeleteDialog } from "../../components/ConfirmDeleteDialog";
import { PlayerPicker } from "../../components/PlayerPicker";
import { AllocatedPaymentForm } from "../../components/AllocatedPaymentForm";
import { paymentTotal, allocationText } from "../../lib/payments";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37] w-full";

const ReceiptModal = ({ paymentId, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/payments/${paymentId}/receipt`).then((r) => setData(r.data)).catch(() => {});
  }, [paymentId]);

  const markSent = async () => {
    try {
      await api.post(`/admin/payments/${paymentId}/receipt-sent`);
      toast.success("Marked as receipt sent");
      onClose(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 py-10 overflow-y-auto" onClick={() => onClose(false)} data-testid="receipt-modal">
      <div className="bg-[#0A0A0A] border border-white/10 p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <h2 className="font-heading text-xl font-black flex items-center gap-2"><Receipt size={18} className="text-[#D4AF37]" /> Payment Receipt</h2>
          <button onClick={() => onClose(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        {data && (
          <>
            <pre className="text-white/70 text-xs whitespace-pre-wrap border border-white/10 bg-[#050505] p-4 font-sans mb-5" data-testid="receipt-message">{data.message}</pre>
            <div className="flex gap-3 flex-wrap">
              {data.wa_link ? (
                <a href={data.wa_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-5 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="receipt-send">
                  <MessageCircle size={14} /> Send on WhatsApp
                </a>
              ) : (
                <span className="text-white/30 text-xs self-center">No guardian WhatsApp number on file</span>
              )}
              {!data.receipt_sent && (
                <button onClick={markSent} className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] text-xs font-bold uppercase tracking-widest px-5 py-3 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid="receipt-mark-sent">
                  <CheckCheck size={14} /> Mark Receipt Sent
                </button>
              )}
              {data.receipt_sent && <span className="text-[#D4AF37] text-xs uppercase tracking-widest self-center">Receipt Sent</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EditPaymentModal = ({ payment, players, onClose, onSaved }) => {
  const [form, setForm] = useState({
    player_id: payment.player_id,
    date: payment.date,
    month: payment.month,
    group: payment.group || 0,
    transport: payment.transport || 0,
    pt: payment.pt || 0,
    previous_dues: payment.previous_dues || 0,
    registration: payment.registration || 0,
    adjustment: payment.adjustment || 0,
    mode: payment.mode || "UPI",
    notes: payment.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/payments/${payment.id}`, {
        ...form,
        group: Number(form.group) || 0, transport: Number(form.transport) || 0,
        pt: Number(form.pt) || 0, previous_dues: Number(form.previous_dues) || 0,
        registration: Number(form.registration) || 0,
        adjustment: Number(form.adjustment) || 0,
      });
      toast.success("Payment updated");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 overflow-y-auto py-10" onClick={onClose} data-testid="edit-payment-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-8 w-full max-w-2xl">
        <div className="flex justify-between items-start mb-5">
          <h2 className="font-heading text-xl font-black">Edit Payment</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-white/50 text-xs uppercase tracking-widest mb-4">
          {payment.player_name} · Recorded {payment.date}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <PlayerPicker players={players} value={form.player_id} onChange={(v) => setForm({ ...form, player_id: v })} />
          <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="edit-pay-date" />
          <input required type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className={inputCls} data-testid="edit-pay-month" />
          {["group", "transport", "pt", "previous_dues", "registration"].map((t) => (
            <input key={t} type="number" min="0" placeholder={FEE_LABELS[t]} value={form[t]} onChange={(e) => setForm({ ...form, [t]: e.target.value })} className={inputCls} data-testid={`edit-pay-${t}`} />
          ))}
          <input type="number" placeholder="Adjustment / Credit" value={form.adjustment} onChange={(e) => setForm({ ...form, adjustment: e.target.value })} className={inputCls} data-testid="edit-pay-adjustment" />
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={inputCls} data-testid="edit-pay-mode">
            {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " lg:col-span-3"} data-testid="edit-pay-notes" />
        </div>
        <div className="flex gap-3 mt-6">
          <button type="submit" disabled={saving} className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-50" data-testid="edit-pay-save">
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <button type="button" onClick={onClose} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]">Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default function Payments() {
  const [month, setMonth] = useState(currentMonth());
  const [payments, setPayments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [receiptFor, setReceiptFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/admin/payments", { params: { month } }).then((r) => setPayments(r.data)).catch(() => {});
  useEffect(() => { load(); }, [month]);
  useEffect(() => { api.get("/admin/players").then((r) => setPlayers(r.data)).catch(() => {}); }, []);

  const doDelete = async () => {
    try {
      await api.delete(`/admin/payments/${deleting.id}`);
      toast.success("Payment deleted");
      setDeleting(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); throw err; }
  };

  return (
    <div data-testid="admin-payments">
      <h1 className="font-heading text-3xl font-black uppercase tracking-tight mb-8">Payments</h1>

      <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-10">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-5">Record a Receipt</p>
        <AllocatedPaymentForm players={players} onSaved={(saved) => { load(); setReceiptFor(saved.id); }} />
      </div>

      <div className="mb-4">
        <MonthNav month={month} onChange={setMonth} testid="payments-month-nav" />
        <p className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mt-3" data-testid="payments-month-summary">{monthLabel(month)} — {payments.length} receipts · {inr(payments.reduce((a, p) => a + paymentTotal(p), 0))} received</p>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
              <th className="p-4">Date</th>
              <th className="p-4">Player</th>
              <th className="p-4 hidden md:table-cell">Breakdown</th>
              <th className="p-4 hidden sm:table-cell">Mode</th>
              <th className="p-4 text-right">Total</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`payment-row-${p.id}`}>
                <td className="p-4 text-white/60">{p.date}{p.edited_at ? <span className="text-white/30 text-[10px] block">edited</span> : null}</td>
                <td className="p-4 font-semibold">{p.player_name}</td>
                <td className="p-4 text-white/50 text-xs hidden md:table-cell">
                  {p.allocated ? allocationText(p) : (["group", "transport", "pt", "previous_dues", "registration", "extra"].filter((t) => p[t] > 0).map((t) => `${FEE_LABELS[t]} ${inr(p[t])}`).join(" · ") || "—")}
                  {p.original_previous_dues ? ` · Prev-Dues ${inr(p.original_previous_dues)} (auto-allocated)` : ""}
                  {p.adjustment ? ` · Credit ${inr(p.adjustment)}` : ""}
                  {p.parent_payment_id ? <span className="text-white/30 block mt-0.5">↳ Auto-allocated from prev-dues</span> : null}
                </td>
                <td className="p-4 text-white/60 hidden sm:table-cell">{p.mode}</td>
                <td className="p-4 text-right text-[#D4AF37] font-semibold">{inr(paymentTotal(p))}</td>
                <td className="p-4 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => setReceiptFor(p.id)} title="Receipt"
                      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest border px-2 py-1 transition-colors duration-150 ${p.receipt_sent ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-white/20 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}
                      data-testid={p.receipt_sent ? `receipt-sent-${p.id}` : `receipt-btn-${p.id}`}>
                      <Receipt size={12} /> {p.receipt_sent ? "Sent ✓" : "Receipt"}
                    </button>
                    <button onClick={() => setEditing(p)} title="Edit payment"
                      className="text-white/50 hover:text-[#D4AF37] p-1.5" data-testid={`payment-edit-${p.id}`}>
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => setDeleting(p)} title="Delete payment"
                      className="text-red-400/70 hover:text-red-400 p-1.5" data-testid={`payment-delete-${p.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-white/40">No payments recorded for {monthLabel(month)}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {receiptFor && <ReceiptModal paymentId={receiptFor} onClose={(changed) => { setReceiptFor(null); if (changed) load(); }} />}
      {editing && !editing.allocated && <EditPaymentModal payment={editing} players={players} onClose={() => setEditing(null)} onSaved={load} />}
      {editing?.allocated && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 overflow-y-auto py-10" onClick={() => setEditing(null)} data-testid="edit-payment-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-8 w-full max-w-3xl">
            <h2 className="font-heading text-xl font-black mb-5">Edit Payment</h2>
            <AllocatedPaymentForm players={players} existing={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          </div>
        </div>
      )}
      <ConfirmDeleteDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={doDelete}
        title="Delete Payment"
        itemLabel={deleting ? `${deleting.player_name} · ${deleting.date} · ${inr(paymentTotal(deleting))}` : ""}
        firstMessage="You're about to delete this payment record permanently. The player's dues will recalculate as if this payment was never received."
        secondMessage="This is the final confirmation. Once deleted, this payment cannot be recovered."
        testid="confirm-payment-delete"
      />
    </div>
  );
}
